import { LoadingButton } from '@mui/lab'
import { Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useBoolean } from 'react-use'

import { TestEvaluationResultsStatusCheckIntegration } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import {
  buildEvaluationConfigurationFromFormValues,
  buildTeamExternalIntegration,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import {
  ObservabilityToolType,
  type EndpointResponse
} from 'types/api/status-checks/status-check'
import { getMessageFromResponseOrError } from 'utils/strings'
import { testStatusCheckEvaluation } from 'utils/team-external-integrations/team-external-integrations-util'

import type { EvaluationResults } from 'types/api/status-checks/evaluation'

export const TestStatusCheckSuccessEvaluation: React.FC<{
  disabled?: boolean
  endpointResponse: EndpointResponse | undefined
  onChanged: (isEvaluationChecked: boolean) => void
}> = ({ disabled = false, endpointResponse, onChanged }) => {
  const { currentTeamId } = useAuth()
  const { isStatusCheckEvaluationValidated } = useStatusCheckDetailsContext()

  const [isLoading, setIsLoading] = useBoolean(false)
  const [evaluationResults, setEvaluationResults] =
    useState<EvaluationResults>()

  const { getValues } = useFormContext<StatusCheckFormValues>()
  const observabilityTool = useWatch<
    StatusCheckFormValues,
    'observabilityTool'
  >({ name: 'observabilityTool' })
  const hasPostMethodEnabled =
    observabilityTool?.type === ObservabilityToolType.CUSTOM

  // Blow away the evaluation results if the validation flag is false.
  useEffect(() => {
    if (isStatusCheckEvaluationValidated) return

    setEvaluationResults(undefined)
  }, [isStatusCheckEvaluationValidated])

  return (
    <Stack>
      <LoadingButton
        color="primary"
        data-testid="test-evaluation-status-check-integration-button"
        disabled={disabled}
        loading={isLoading}
        size="small"
        sx={{ marginBottom: 2, width: 'fit-content' }}
        variant="contained"
        onClick={async () => {
          if (!endpointResponse) return

          setIsLoading(true)

          const formValues = getValues()

          await testStatusCheckEvaluation({
            endpointConfiguration: hasPostMethodEnabled
              ? formValues.endpointConfiguration
              : undefined,
            evaluationConfiguration:
              buildEvaluationConfigurationFromFormValues(formValues),
            endpointResponse,
            teamId: currentTeamId,
            integration: buildTeamExternalIntegration(formValues),
            onSuccess: (evaluationResults) => {
              setIsLoading(false)
              setEvaluationResults(evaluationResults)
              onChanged(true)
            },
            onError: (err) => {
              setIsLoading(false)
              onChanged(false)
              toast({
                type: 'error',
                text: getMessageFromResponseOrError(
                  err,
                  'There was an error while testing this evaluation. Please try again later.'
                )
              })
            }
          })
        }}
      >
        Test Evaluation
      </LoadingButton>
      {!isLoading && endpointResponse && evaluationResults && (
        <TestEvaluationResultsStatusCheckIntegration
          endpointResponse={endpointResponse}
          evaluationResults={evaluationResults}
          integration={null}
        />
      )}
    </Stack>
  )
}
